import React, { useState, useEffect } from 'react';
import { Github, X, FileText, AlertCircle } from 'lucide-react';
import { API } from '../App';
import RepoSelector from './RepoSelector';
import FileTreeBrowser from './FileTreeBrowser';

export function GitHubContextAttacher({ onContextReady, githubContext, setGithubContext }) {
  const [connected, setConnected] = useState(false);
  const [checking, setChecking] = useState(true);
  const [showRepoSelector, setShowRepoSelector] = useState(false);
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState(null);

  useEffect(() => {
    API.get('/api/github/status')
      .then(r => setConnected(r.data.connected))
      .catch(() => setConnected(false))
      .finally(() => setChecking(false));
  }, []);

  const handleRepoSelected = (repo) => {
    setSelectedRepo(repo);
    setShowRepoSelector(false);
    setShowFileBrowser(true);
  };

  const handleFilesConfirmed = (files, repo) => {
    setShowFileBrowser(false);
    const context = {
      repo: repo.full_name,
      paths: files.map(f => f.path),
      files: files,
    };
    setGithubContext(context);
    if (onContextReady) onContextReady(context);
  };

  const removeFile = (path) => {
    if (!githubContext) return;
    const newFiles = githubContext.files.filter(f => f.path !== path);
    const newPaths = githubContext.paths.filter(p => p !== path);
    if (newFiles.length === 0) {
      setGithubContext(null);
    } else {
      setGithubContext({ ...githubContext, files: newFiles, paths: newPaths });
    }
  };

  if (checking) return null;

  if (!connected) {
    return (
      <div className="mb-6" data-testid="github-context-attacher">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold font-['Barlow_Condensed']">GitHub Context</h3>
        </div>
        <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] flex items-center gap-2">
          <Github className="w-4 h-4 text-[#A0A0A0]" />
          <span className="text-xs text-[#A0A0A0]">
            Connect GitHub in <a href="/settings" className="text-[#FF4D00] hover:underline">Settings</a> to attach code context
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6" data-testid="github-context-attacher">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold font-['Barlow_Condensed']">GitHub Context</h3>
        <button
          onClick={() => setShowRepoSelector(true)}
          className="text-xs text-[#FF4D00] flex items-center gap-1 min-h-[44px] px-2"
          data-testid="attach-github-btn"
        >
          <Github className="w-3 h-3" /> Attach Code
        </button>
      </div>

      {githubContext && githubContext.files?.length > 0 ? (
        <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
          <div className="flex items-center gap-2 mb-2">
            <Github className="w-3 h-3 text-[#FF4D00]" />
            <span className="text-[10px] font-mono text-[#FF4D00]">{githubContext.repo}</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {githubContext.files.map(f => (
              <span key={f.path} className="text-[10px] px-2 py-1 bg-[#0F0F0F] border border-[#FF4D00]/20 text-[#FF4D00] flex items-center gap-1">
                <FileText className="w-2.5 h-2.5" />
                {f.name}
                {f.size > 10240 && <AlertCircle className="w-2.5 h-2.5 text-[#F59E0B]" />}
                <button onClick={() => removeFile(f.path)} className="text-[#A0A0A0] hover:text-[#EF4444]">
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-xs text-[#A0A0A0]">
          No code context attached. Select files from your GitHub repos to give the committee more context.
        </p>
      )}

      <RepoSelector
        open={showRepoSelector}
        onClose={() => setShowRepoSelector(false)}
        onSelectRepo={handleRepoSelected}
      />

      <FileTreeBrowser
        open={showFileBrowser}
        onClose={() => setShowFileBrowser(false)}
        repo={selectedRepo}
        onConfirm={handleFilesConfirmed}
      />
    </div>
  );
}
